import { inject, Injectable } from '@angular/core'
import { UserState } from '../data-access/state/user.state'
import { EvenementHttp } from '../data-access/http/evenement.http'
import { Observable } from 'rxjs'
import { CodeTypeEvenement, EvenementDto } from '../data-access/dto/evenement.dto'

@Injectable({ providedIn: 'root' })
export class EvenementDomainService {
  private userState = inject(UserState)
  private evenementService = inject(EvenementHttp)

  // Actions

  createSelectionChantierEvenement(
    ancienCodeChantier: string | null,
    nouveauCodeChantier: string,
    statutRegistre: 'AVEC_DONNEES' | 'VIDE',
  ): Observable<EvenementDto> {
    const connectedUser = this.userState.store.selectSnapshot(UserState.getConnectedUser)

    const message = !ancienCodeChantier
      ? `L'utilisateur ${connectedUser.idUtilisateurSi} a accédé au chantier ${nouveauCodeChantier}`
      : `L'utilisateur ${connectedUser.idUtilisateurSi} est passé du chantier ${ancienCodeChantier} au chantier ${nouveauCodeChantier}`

    const event = new EventBuilder()
      .utilisateurId(connectedUser.id)
      .codeTypeEvenement('SELECTION_CHANTIER')
      .codeChantier(nouveauCodeChantier)
      .commentaire(`${message}. Le statut du registre est ${statutRegistre}`)

    return this.evenementService.createEvenement(event.build())
  }

  createModificationTableauEvenement(
    compagnonId: number,
    codeChantier: string,
    champ: string,
    ancienneValeur: string | null,
    nouvelleValeur: string | null,
  ): Observable<EvenementDto> {
    const connectedUser = this.userState.store.selectSnapshot(UserState.getConnectedUser)
    const ancienneValeurAffichee = ancienneValeur || 'Vide'
    const nouvelleValeurAffichee = nouvelleValeur || 'Vide'

    const message = `L'utilisateur ${connectedUser.idUtilisateurSi} a modifié le champ "${champ}" du compagnon ${compagnonId} depuis le tableau sur le chantier ${codeChantier} : "${ancienneValeurAffichee}" → "${nouvelleValeurAffichee}"`

    const event = new EventBuilder()
      .utilisateurId(connectedUser.id)
      .codeTypeEvenement('MODIFICATION_TABLEAU_RPC')
      .codeChantier(codeChantier)
      .objet(compagnonId.toString())
      .objetChamp(champ)
      .objetAncienneValeur(ancienneValeurAffichee)
      .objetNouvelleValeur(nouvelleValeurAffichee)
      .commentaire(message)

    return this.evenementService.createEvenement(event.build())
  }

  createModificationModaleEvenement(
    compagnonId: number,
    codeChantier: string,
    champ: string,
    ancienneValeur: string | null,
    nouvelleValeur: string | null,
    codeTypeEvenement: CodeTypeEvenement = 'MODIFICATION_MODALE_RPC',
  ): Observable<EvenementDto> {
    const connectedUser = this.userState.store.selectSnapshot(UserState.getConnectedUser)
    const ancienneValeurAffichee = ancienneValeur || 'Vide'
    const nouvelleValeurAffichee = nouvelleValeur || 'Vide'

    const message = `L'utilisateur ${connectedUser.idUtilisateurSi} a modifié le champ "${champ}" du compagnon ${compagnonId} depuis la modale sur le chantier ${codeChantier} : "${ancienneValeurAffichee}" → "${nouvelleValeurAffichee}"`

    const event = new EventBuilder()
      .utilisateurId(connectedUser.id)
      .codeTypeEvenement(codeTypeEvenement)
      .codeChantier(codeChantier)
      .objet(compagnonId.toString())
      .objetChamp(champ)
      .objetAncienneValeur(ancienneValeurAffichee)
      .objetNouvelleValeur(nouvelleValeurAffichee)
      .commentaire(message)

    return this.evenementService.createEvenement(event.build())
  }
}

class EventBuilder {
  private _codeTypeEvenement: CodeTypeEvenement
  private _date: Date
  private _utilisateurId: number
  private _commentaire: string
  private _codeChantier: string
  private _objet: string
  private _objetChamp: string
  private _objetNouvelleValeur: string
  private _objetAncienneValeur: string

  constructor() {
    this._date = new Date()
  }

  codeTypeEvenement(codeTypeEvenement: CodeTypeEvenement): EventBuilder {
    this._codeTypeEvenement = codeTypeEvenement
    return this
  }

  utilisateurId(utilisateurId: number): EventBuilder {
    this._utilisateurId = utilisateurId
    return this
  }

  commentaire(commentaire: string): EventBuilder {
    const now = new Date()
    this._commentaire = commentaire + `, le ${now.toLocaleDateString()} à ${now.toLocaleTimeString()}`
    return this
  }

  codeChantier(codeChantier: string): EventBuilder {
    this._codeChantier = codeChantier
    return this
  }

  objet(objet: string): EventBuilder {
    this._objet = objet
    return this
  }

  objetChamp(objetChamp: string): EventBuilder {
    this._objetChamp = objetChamp
    return this
  }

  objetNouvelleValeur(value: string): EventBuilder {
    this._objetNouvelleValeur = value
    return this
  }

  objetAncienneValeur(value: string): EventBuilder {
    this._objetAncienneValeur = value
    return this
  }

  build(): EvenementDto {
    return {
      date: this._date,
      utilisateurId: this._utilisateurId,
      codeChantier: this._codeChantier,
      commentaire: this._commentaire,
      codeTypeEvenement: this._codeTypeEvenement,
      objet: this._objet,
      objetChamp: this._objetChamp,
      objetNouvelleValeur: this._objetNouvelleValeur,
      objetAncienneValeur: this._objetAncienneValeur,
    }
  }
}
